/**
 * Voice chat, per game (players and watchers), opt-in. Audio goes browser to browser over WebRTC,
 * one connection per other member (a mesh: fine for a table of up to 8). The server only relays
 * the setup messages (`voice:signal`) and says who is in (`voice:members`).
 *
 * Whoever joins sends an offer to everyone already in; members only answer. ICE candidates that
 * arrive before the other side's description are queued. Speaking is measured locally from each
 * stream's level (an AnalyserNode, not connected to the speakers; playback is an audio element).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import {
  t,
  type ClientToServerEvents,
  type IceServer,
  type Notice,
  type PlayerId,
  type ServerToClientEvents,
  type VoiceSignal,
} from '@kadice/shared';

type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

const LEVEL_POLL_MS = 150;
/** RMS level (0..1) above which someone counts as speaking. */
const SPEAKING_LEVEL = 0.04;

interface Peer {
  pc: RTCPeerConnection;
  audio: HTMLAudioElement;
  pending: RTCIceCandidateInit[];
  level: AnalyserNode | null;
}

class VoiceSession {
  private readonly peers = new Map<PlayerId, Peer>();
  private readonly ctx = new AudioContext();
  private readonly local: AnalyserNode;
  private readonly timer: ReturnType<typeof setInterval>;
  private speaking = new Set<PlayerId>();
  private closed = false;

  constructor(
    private readonly socket: GameSocket,
    private readonly me: PlayerId,
    private readonly iceServers: IceServer[],
    private readonly stream: MediaStream,
    private readonly onSpeaking: (ids: ReadonlySet<PlayerId>) => void,
  ) {
    void this.ctx.resume();
    this.local = this.analyse(stream);
    this.timer = setInterval(() => this.poll(), LEVEL_POLL_MS);
  }

  /** Just joined: offer to everyone already in. */
  start(peerIds: PlayerId[]) {
    for (const id of peerIds) void this.offer(id);
  }

  set muted(muted: boolean) {
    this.stream.getAudioTracks().forEach((track) => (track.enabled = !muted));
  }

  get muted() {
    return this.stream.getAudioTracks().every((track) => !track.enabled);
  }

  async signal(from: PlayerId, data: VoiceSignal) {
    if (this.closed) return;
    try {
      if (data.type === 'ice') {
        const peer = this.peers.get(from);
        if (!peer) return;
        if (peer.pc.remoteDescription) await peer.pc.addIceCandidate(data.candidate);
        else peer.pending.push(data.candidate);
      } else if (data.type === 'offer') {
        this.drop(from); // a fresh offer replaces any old connection (they rejoined)
        const peer = this.connect(from);
        await peer.pc.setRemoteDescription({ type: 'offer', sdp: data.sdp });
        await this.flush(peer);
        await peer.pc.setLocalDescription(await peer.pc.createAnswer());
        this.send(from, { type: 'answer', sdp: peer.pc.localDescription!.sdp });
      } else {
        const peer = this.peers.get(from);
        if (!peer || peer.pc.signalingState !== 'have-local-offer') return;
        await peer.pc.setRemoteDescription({ type: 'answer', sdp: data.sdp });
        await this.flush(peer);
      }
    } catch {
      this.drop(from); // a broken setup: that one connection stays down until someone rejoins
    }
  }

  /** The members list changed: drop connections to anyone who left. */
  sync(members: readonly PlayerId[]) {
    for (const id of [...this.peers.keys()]) if (!members.includes(id)) this.drop(id);
  }

  close() {
    this.closed = true;
    clearInterval(this.timer);
    for (const id of [...this.peers.keys()]) this.drop(id);
    this.stream.getTracks().forEach((track) => track.stop());
    void this.ctx.close();
  }

  private async offer(id: PlayerId) {
    const peer = this.connect(id);
    try {
      await peer.pc.setLocalDescription(await peer.pc.createOffer());
      this.send(id, { type: 'offer', sdp: peer.pc.localDescription!.sdp });
    } catch {
      this.drop(id);
    }
  }

  private connect(id: PlayerId): Peer {
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    const audio = new Audio();
    audio.autoplay = true;
    const peer: Peer = { pc, audio, pending: [], level: null };
    this.peers.set(id, peer);
    this.stream.getTracks().forEach((track) => pc.addTrack(track, this.stream));
    pc.onicecandidate = (e) => {
      if (e.candidate) this.send(id, { type: 'ice', candidate: e.candidate.toJSON() as IceCandidate });
    };
    pc.ontrack = (e) => {
      const remote = e.streams[0] ?? new MediaStream([e.track]);
      audio.srcObject = remote;
      void audio.play().catch(() => {});
      peer.level = this.analyse(remote);
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') this.drop(id);
    };
    return peer;
  }

  private async flush(peer: Peer) {
    for (const candidate of peer.pending.splice(0)) await peer.pc.addIceCandidate(candidate);
  }

  private drop(id: PlayerId) {
    const peer = this.peers.get(id);
    if (!peer) return;
    this.peers.delete(id);
    peer.pc.close();
    peer.audio.srcObject = null;
  }

  private send(to: PlayerId, data: VoiceSignal) {
    this.socket.emit('voice:signal', { to, data }, () => {});
  }

  private analyse(stream: MediaStream): AnalyserNode {
    const node = this.ctx.createAnalyser();
    node.fftSize = 512;
    this.ctx.createMediaStreamSource(stream).connect(node);
    return node;
  }

  private poll() {
    const now = new Set<PlayerId>();
    if (!this.muted && loud(this.local)) now.add(this.me);
    for (const [id, peer] of this.peers) if (peer.level && loud(peer.level)) now.add(id);
    if (now.size !== this.speaking.size || [...now].some((id) => !this.speaking.has(id))) {
      this.speaking = now;
      this.onSpeaking(now);
    }
  }
}

type IceCandidate = Extract<VoiceSignal, { type: 'ice' }>['candidate'];

const buffer = new Uint8Array(512);
function loud(node: AnalyserNode): boolean {
  node.getByteTimeDomainData(buffer);
  let sum = 0;
  for (let i = 0; i < node.fftSize; i++) sum += ((buffer[i]! - 128) / 128) ** 2;
  return Math.sqrt(sum / node.fftSize) > SPEAKING_LEVEL;
}

export const voiceSupported = () => typeof RTCPeerConnection !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

/** What the client's socket hands to voice chat. */
export interface VoiceEvents {
  signal(from: PlayerId, data: VoiceSignal): void;
  end(): void;
}

export interface Voice {
  /** Who is in your game's voice chat (you included once joined). */
  members: readonly PlayerId[];
  joined: boolean;
  joining: boolean;
  muted: boolean;
  speaking: ReadonlySet<PlayerId>;
  join(): void;
  leave(): void;
  toggleMute(): void;
  /** For the client's socket listeners. */
  events: VoiceEvents;
}

/**
 * Your voice chat. `request` is the client's acked request; `members` and `gameId` come from the
 * server. Leaving the game, a dropped connection, or joining in another tab ends it.
 */
export function useVoice(opts: {
  socket: React.RefObject<GameSocket | null>;
  iceServers: React.RefObject<IceServer[]>;
  me: PlayerId | null;
  gameId: number | null;
  members: { gameId: number; ids: PlayerId[] } | null;
  join: () => Promise<{ peers: PlayerId[] } | undefined>;
  notify: (n: Notice) => void;
}): Voice {
  const { socket, iceServers, me, gameId, members, join: requestJoin, notify } = opts;
  const session = useRef<VoiceSession | null>(null);
  const [joinedGame, setJoinedGame] = useState<number | null>(null);
  const [joining, setJoining] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState<ReadonlySet<PlayerId>>(new Set());

  const end = useCallback(() => {
    session.current?.close();
    session.current = null;
    setJoinedGame(null);
    setMuted(false);
    setSpeaking(new Set());
  }, []);

  const join = useCallback(async () => {
    const s = socket.current;
    if (session.current || joining || me === null || gameId === null || !s) return;
    setJoining(true);
    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
      } catch {
        notify({ kind: 'error', text: t('voice.micDenied') });
        return;
      }
      const reply = await requestJoin();
      if (!reply) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      session.current = new VoiceSession(s, me, iceServers.current, stream, setSpeaking);
      session.current.start(reply.peers);
      setJoinedGame(gameId);
      setMuted(false);
      setSpeaking(new Set());
    } finally {
      setJoining(false);
    }
  }, [socket, iceServers, joining, me, gameId, requestJoin, notify]);

  const leave = useCallback(() => {
    if (!session.current) return;
    end();
    socket.current?.emit('voice:leave', () => {});
  }, [end, socket]);

  // Socket events, for the client to pass on: signals for the session; the server ending it
  // (another tab joined, you left the game); a dropped connection (the server took you out).
  const events = useMemo<VoiceEvents>(
    () => ({ signal: (from, data) => void session.current?.signal(from, data), end }),
    [end],
  );

  // Left the game (or switched to another): voice ends with it (`joined` below already says so).
  const inThisGame = joinedGame !== null && joinedGame === gameId;
  useEffect(() => {
    if (!inThisGame && session.current) {
      session.current.close();
      session.current = null;
    }
  }, [inThisGame]);

  useEffect(() => {
    if (members && members.gameId === joinedGame) session.current?.sync(members.ids);
  }, [members, joinedGame]);

  useEffect(() => end, [end]);

  const toggleMute = useCallback(() => {
    if (!session.current) return;
    session.current.muted = !muted;
    setMuted(!muted);
  }, [muted]);

  return useMemo(
    () => ({
      members: members && members.gameId === gameId ? members.ids : NO_MEMBERS,
      joined: inThisGame,
      joining,
      muted: inThisGame && muted,
      speaking: inThisGame ? speaking : NO_ONE,
      join: () => void join(),
      leave,
      toggleMute,
      events,
    }),
    [members, gameId, inThisGame, joining, muted, speaking, join, leave, toggleMute, events],
  );
}

const NO_MEMBERS: readonly PlayerId[] = [];
const NO_ONE: ReadonlySet<PlayerId> = new Set();
